<template>
  <div class="bs-page">
    <div class="bs-grid-overlay" aria-hidden="true"></div>
    <div class="bs-glow bs-glow-one" aria-hidden="true"></div>

    <StandardNavBar />

    <main>
      <PageHero
        eyebrow="Quality, safety and training"
        title="Good service is not only"
        subtitle="about completing the work."
        :lead="qualitySafety.belief"
        :image="heroImage.src"
        :image-alt="heroImage.alt"
        image-caption="Full harness, helmet and an independent working and backup line — the standard set-up on every rope job."
      />

      <!-- ============ THREE PILLARS ============ -->
      <section class="section-pad pillars-section shell">
        <div class="section-head">
          <div>
            <p class="eyebrow">
              <span class="eyebrow-line"></span>
              How we work
            </p>
            <h2 class="display-2">
              Three things we hold
              <span>ourselves to on every job.</span>
            </h2>
          </div>

          <p class="lead">
            Quality of work, safety on site, and the training that makes both possible.
          </p>
        </div>

        <div class="pillar-grid">
          <article
            v-for="(pillar, index) in qualitySafety.pillars"
            :key="pillar.key"
            class="card pillar-card"
          >
            <div class="pillar-top">
              <div class="icon-tile">
                <component :is="pillarIcons[index]" />
              </div>
              <span>{{ String(index + 1).padStart(2, "0") }}</span>
            </div>

            <h3>{{ pillar.title }}</h3>
            <p>{{ pillar.body }}</p>

            <ul class="check-list">
              <li v-for="point in pillarPoints[pillar.key]" :key="point">
                <CircleCheckBig />
                <span>{{ point }}</span>
              </li>
            </ul>
          </article>
        </div>
      </section>

      <!-- ============ PRODUCTIVITY ============ -->
      <section class="productivity-section">
        <div class="shell productivity-inner">
          <div class="productivity-copy">
            <p class="eyebrow">
              <span class="eyebrow-line"></span>
              Continuous improvement
            </p>

            <h2 class="display-2">
              Better methods,
              <span>fewer problems on site.</span>
            </h2>

            <p class="body-copy">{{ qualitySafety.productivity }}</p>
          </div>

          <ul class="method-list">
            <li v-for="method in methods" :key="method.title">
              <component :is="method.icon" />
              <div>
                <strong>{{ method.title }}</strong>
                <span>{{ method.detail }}</span>
              </div>
            </li>
          </ul>
        </div>
      </section>

      <!-- ============ GOAL ============ -->
      <section class="goal-section shell">
        <div class="goal-card">
          <span class="goal-label">Our goal</span>
          <p>{{ qualitySafety.goal }}</p>
        </div>
      </section>

      <SectionCta
        eyebrow="Talk to us"
        heading="Want to know how we would run your project?"
        body="Tell us the scope and the building. We will walk you through the method, the controls and how the work would be reported back to you."
      />
    </main>

    <SiteFooter />
  </div>
</template>

<script>
import {
  Cable,
  CircleCheckBig,
  ClipboardCheck,
  GraduationCap,
  ShieldCheck,
  Wrench,
} from "lucide-vue-next";

import PageHero from "@/components/PageHero.vue";
import SectionCta from "@/components/SectionCta.vue";
import SiteFooter from "@/components/SiteFooter.vue";
import StandardNavBar from "@/components/StandardNavBar.vue";
import { qualitySafety } from "@/content/company-content";
import { pageHeroes } from "@/content/gallery";

export default {
  name: "QualitySafetyPage",

  components: {
    CircleCheckBig,
    PageHero,
    SectionCta,
    SiteFooter,
    StandardNavBar,
  },

  data() {
    return {
      qualitySafety,
      heroImage: pageHeroes.qualitySafety,

      pillarIcons: [ClipboardCheck, ShieldCheck, GraduationCap],

      // Stages and controls named in the copy, broken out so they are scannable.
      pillarPoints: {
        quality: [
          "Initial site inspection",
          "Planning",
          "Execution",
          "Reporting",
          "Final handover",
        ],
        safety: [
          "Site-specific risk assessments",
          "Safe work procedures",
          "Permit-to-work requirements",
          "Applicable safety regulations",
        ],
        training: [
          "Relevant training courses",
          "Safety briefings",
          "Technical workshops",
        ],
      },

      methods: [
        {
          title: "Rope access systems",
          detail: "Reaching difficult elevations without heavy access setup.",
          icon: Cable,
        },
        {
          title: "Suitable equipment",
          detail: "Matched to the task, the site and the working conditions.",
          icon: Wrench,
        },
        {
          title: "More efficient work methods",
          detail: "Sequencing and planning that reduce downtime on site.",
          icon: ClipboardCheck,
        },
      ],
    };
  },
};
</script>

<style scoped>
/* ---------- pillars ---------- */

.pillar-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 52px;
}

.pillar-card {
  display: flex;
  flex-direction: column;
  padding: 32px;
}

.pillar-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.pillar-top span {
  color: #b6c9db;
  font-family: var(--font-display);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.1em;
}

.pillar-card h3 {
  margin: 26px 0 0;
  color: var(--ink);
  font-family: var(--font-display);
  font-size: 24px;
  line-height: 1.2;
  letter-spacing: -0.035em;
}

.pillar-card > p {
  margin: 16px 0 0;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.85;
}

.pillar-card .check-list {
  margin-top: 26px;
  padding-top: 24px;
  border-top: 1px solid var(--line-soft);
}

/* ---------- productivity ---------- */

.productivity-section {
  position: relative;
  z-index: 2;
  padding: 110px 0;
  border-top: 1px solid rgba(11, 37, 69, 0.08);
  border-bottom: 1px solid rgba(11, 37, 69, 0.08);
  background: #f0f6fc;
}

.productivity-inner {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(360px, 0.86fr);
  gap: 60px;
  align-items: center;
}

.productivity-copy .display-2 {
  margin-bottom: 24px;
}

.productivity-copy .body-copy {
  max-width: 640px;
  margin: 0;
}

.method-list {
  display: grid;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.method-list li {
  display: grid;
  grid-template-columns: 44px 1fr;
  align-items: center;
  gap: 18px;
  padding: 24px;
  border: 1px solid var(--line);
  background: var(--surface);
  box-shadow: var(--shadow-card);
}

.method-list svg {
  width: 24px;
  height: 24px;
  color: var(--blue);
}

.method-list strong {
  display: block;
  color: var(--ink);
  font-family: var(--font-display);
  font-size: 17px;
  letter-spacing: -0.02em;
}

.method-list span {
  display: block;
  margin-top: 7px;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.6;
}

/* ---------- goal ---------- */

.goal-section {
  position: relative;
  z-index: 2;
  padding: 96px 0;
}

.goal-card {
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 32px;
  align-items: center;
  padding: 44px;
  border: 1px solid var(--line);
  border-left: 3px solid var(--blue);
  background: var(--surface);
  box-shadow: var(--shadow-card);
}

.goal-label {
  color: var(--muted-light);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.goal-card p {
  margin: 0;
  color: var(--ink);
  font-family: var(--font-display);
  font-size: clamp(21px, 2.2vw, 30px);
  line-height: 1.36;
  letter-spacing: -0.03em;
}

@media (max-width: 1080px) {
  .pillar-grid {
    grid-template-columns: 1fr;
  }

  .productivity-inner {
    grid-template-columns: 1fr;
    gap: 36px;
  }
}

@media (max-width: 760px) {
  .pillar-card {
    padding: 26px;
  }

  .productivity-section {
    padding: 76px 0;
  }

  .goal-section {
    padding: 72px 0;
  }

  .goal-card {
    grid-template-columns: 1fr;
    gap: 18px;
    padding: 28px;
  }
}
</style>
